/**
 * Category Modal Component
 * Two-step: pick service types (multi), then pick issue tags (multi).
 * Both steps support an "Other" describe-the-issue note.
 */

import { useState } from 'react';
import type { Category, ServiceType, ServiceTypeOption } from '../../types';

interface CategoryModalProps {
  serviceTypes: ServiceTypeOption[];
  categories: Category[];
  onClose: () => void;
  onConfirm: (serviceTypes: ServiceType[], categories: string[], closeNote?: string) => void;
  isSending: boolean;
}

export function CategoryModal({
  serviceTypes,
  categories,
  onClose,
  onConfirm,
  isSending,
}: CategoryModalProps) {
  const [selectedServices, setSelectedServices] = useState<ServiceType[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [serviceOtherNote, setServiceOtherNote] = useState('');
  const [otherNote, setOtherNote] = useState('');

  const toggleService = (id: ServiceType) => {
    setSelectedServices(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleCategory = (id: string) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    if (selectedServices.length === 0) {
      alert('Please select at least one service type');
      return;
    }
    if (selected.length === 0) {
      alert('Please select at least one category');
      return;
    }
    // Combine the two "Describe the issue" notes into a single closeNote.
    // Each is only included if its corresponding "Other" is actually selected.
    const parts: string[] = [];
    if (selectedServices.includes('other') && serviceOtherNote.trim()) {
      parts.push(`Service: ${serviceOtherNote.trim()}`);
    }
    if (selected.includes('other') && otherNote.trim()) {
      parts.push(`Issue: ${otherNote.trim()}`);
    }
    const closeNote = parts.length > 0 ? parts.join(' / ') : undefined;
    onConfirm(selectedServices, selected, closeNote);
  };

  const canConfirm = selectedServices.length > 0 && selected.length > 0 && !isSending;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col safe-area-bottom">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Close Ticket</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Step 1 — Service types (multi-select) */}
          <p className="text-gray-700 font-medium mb-2">
            1. Which service was the user asking about? (select all that apply)
          </p>
          <div className="space-y-2 mb-5">
            {serviceTypes.map(svc => (
              <div key={svc.id}>
                <button
                  onClick={() => toggleService(svc.id)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    selectedServices.includes(svc.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        selectedServices.includes(svc.id)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedServices.includes(svc.id) && (
                        <span className="text-white text-sm">✓</span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{svc.label}</div>
                      {svc.description && (
                        <div className="text-sm text-gray-500">{svc.description}</div>
                      )}
                    </div>
                  </div>
                </button>

                {/* Free-text note — only when service "Other" is selected */}
                {svc.id === 'other' && selectedServices.includes('other') && (
                  <textarea
                    value={serviceOtherNote}
                    onChange={e => setServiceOtherNote(e.target.value)}
                    placeholder="Describe the service or issue..."
                    rows={3}
                    className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-blue-400 resize-none"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 2 — Issue tags (multi-select) */}
          <p className="text-gray-700 font-medium mb-2">
            2. What was the issue? (select all that apply)
          </p>
          <div className="space-y-2">
            {categories.map(cat => (
              <div key={cat.id}>
                <button
                  onClick={() => toggleCategory(cat.id)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    selected.includes(cat.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        selected.includes(cat.id)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {selected.includes(cat.id) && (
                        <span className="text-white text-sm">✓</span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium">{cat.label}</div>
                      {cat.description && (
                        <div className="text-sm text-gray-500">
                          {cat.description}
                        </div>
                      )}
                    </div>
                  </div>
                </button>

                {/* Free-text note — only shown when category "Other" is selected */}
                {cat.id === 'other' && selected.includes('other') && (
                  <textarea
                    value={otherNote}
                    onChange={e => setOtherNote(e.target.value)}
                    placeholder="Describe the issue..."
                    rows={3}
                    className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-blue-400 resize-none"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition disabled:opacity-50"
          >
            {isSending ? 'Closing...' : 'Close Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
